'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo, useRef } from 'react';
import { Button, Image } from '@udemy-v2/react-core-components';
import { publishEvent, TrackImpression } from '@udemy-v2/event-tracking';
import { useI18n } from '@udemy-v2/i18n';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import classNames from 'classnames';
import { InstructorCommunityBannerInfoByInstructorIdQuery } from '@/generated/graphql';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { readInstructorCommunityId } from '@/lib/hooks/use-instructor-community-banner-info';
import { InstructorCommunityJoinSelected, InstructorCommunityPromotionPresented, InstructorCommunityPromotionSelected, InstructorCommunityUiRegion } from '@/features/course-landing-page/events/events';
import styles from './instructor-community-banner.module.scss';
export type InstructorCommunityBannerInfo = NonNullable<InstructorCommunityBannerInfoByInstructorIdQuery['instructorCommunityBannerInfoByInstructorId']>;
const BANNER_TEXT_COLOR = {
  DARK: 'var(--applied-text-default)',
  LIGHT: '#E9EAF2'
} as const;
export interface InstructorCommunityBannerProps {
  instructorId: number | string;
  instructorName: string;
  instructorUrl?: string;
  bannerInfo: InstructorCommunityBannerInfo;
}
export function InstructorCommunityBanner(t0) {
  const $ = _c(53);
  const {
    instructorId,
    instructorName,
    instructorUrl,
    bannerInfo
  } = t0;
  const {
    gettext,
    interpolate
  } = useI18n();
  const isMobile = useMatchMedia("sm-max");
  const course = useClpCourse();
  const hasTrackedImpression = useRef(false);
  let t1;
  if ($[0] !== bannerInfo) {
    t1 = readInstructorCommunityId(bannerInfo);
    $[0] = bannerInfo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const communityId = t1;
  let t2;
  if ($[2] !== communityId || $[3] !== course.id || $[4] !== instructorId) {
    t2 = communityId !== undefined ? {
      instructorId: Number(instructorId),
      communityId,
      courseId: Number(course.id),
      uiRegion: InstructorCommunityUiRegion.BANNER
    } : undefined;
    $[2] = communityId;
    $[3] = course.id;
    $[4] = instructorId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const bannerEventArgs = t2;
  let t3;
  if ($[6] !== bannerEventArgs) {
    t3 = () => {
      if (!bannerEventArgs || hasTrackedImpression.current) {
        return;
      }
      hasTrackedImpression.current = true;
      publishEvent(new InstructorCommunityPromotionPresented(bannerEventArgs));
    };
    $[6] = bannerEventArgs;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleImpression = t3;
  let t4;
  if ($[8] !== bannerEventArgs) {
    t4 = () => {
      if (!bannerEventArgs) {
        return;
      }
      publishEvent(new InstructorCommunityJoinSelected(bannerEventArgs));
    };
    $[8] = bannerEventArgs;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleJoinSelected = t4;
  let t5;
  if ($[10] !== bannerEventArgs) {
    t5 = () => {
      if (!bannerEventArgs) {
        return;
      }
      publishEvent(new InstructorCommunityPromotionSelected(bannerEventArgs));
    };
    $[10] = bannerEventArgs;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handlePromotionSelected = t5;
  const bannerImageUrl = isMobile ? bannerInfo.mobileCommunityBannerImage : bannerInfo.communityBannerImage;
  if (!bannerImageUrl) {
    return null;
  }
  const textColor = isMobile ? BANNER_TEXT_COLOR.LIGHT : BANNER_TEXT_COLOR[bannerInfo.communityBannerTextColorPreference ?? "DARK"];
  const isLightText = textColor === BANNER_TEXT_COLOR.LIGHT;
  let t6;
  if ($[12] !== gettext || $[13] !== instructorName || $[14] !== interpolate) {
    t6 = interpolate(gettext("Join %(instructorName)s's Learning Community"), {
      instructorName
    }, true);
    $[12] = gettext;
    $[13] = instructorName;
    $[14] = interpolate;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const bannerTitle = t6;
  let t7;
  if ($[16] !== bannerInfo.isSubscribed || $[17] !== gettext) {
    t7 = bannerInfo.isSubscribed ? gettext("Go to community") : gettext("Join my community");
    $[16] = bannerInfo.isSubscribed;
    $[17] = gettext;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const primaryButtonText = t7;
  const primaryButtonHref = bannerInfo.isSubscribed ? bannerInfo.communityUrl : bannerInfo.communityPaywallUrl;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classNames("ud-heading-lg", styles["banner-title"]);
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== bannerTitle) {
    t9 = <h3 className={t8}>{bannerTitle}</h3>;
    $[20] = bannerTitle;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== bannerInfo.description) {
    t10 = bannerInfo.description && <p className={classNames("ud-text-sm", styles["banner-description"])}>{bannerInfo.description}</p>;
    $[22] = bannerInfo.description;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = !isMobile;
  let t12;
  if ($[24] !== t11) {
    t12 = classNames(styles["banner-buttons"], {
      [styles["banner-buttons-desktop"]]: t11
    });
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== handleJoinSelected || $[27] !== primaryButtonHref || $[28] !== primaryButtonText) {
    t13 = <Button udStyle="primary" size="small" componentClass="a" href={primaryButtonHref} onClick={handleJoinSelected}>{primaryButtonText}</Button>;
    $[26] = handleJoinSelected;
    $[27] = primaryButtonHref;
    $[28] = primaryButtonText;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = isLightText ? "knockout-outline" : "secondary";
  let t15;
  if ($[30] !== gettext) {
    t15 = gettext("Learn more");
    $[30] = gettext;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== handlePromotionSelected || $[33] !== instructorUrl || $[34] !== t14 || $[35] !== t15) {
    t16 = <Button udStyle={t14} size="small" componentClass="a" href={instructorUrl} onClick={handlePromotionSelected}>{t15}</Button>;
    $[32] = handlePromotionSelected;
    $[33] = instructorUrl;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t12 || $[38] !== t13 || $[39] !== t16) {
    t17 = <div className={t12}>{t13}{t16}</div>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t10 || $[42] !== t17 || $[43] !== t9) {
    t18 = <>{t9}{t10}{t17}</>;
    $[41] = t10;
    $[42] = t17;
    $[43] = t9;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const bannerContent = t18;
  let t19;
  if ($[45] !== bannerContent || $[46] !== bannerImageUrl || $[47] !== isMobile || $[48] !== textColor) {
    t19 = isMobile ? <div className={styles["banner-container-mobile"]} data-purpose="instructor-community-banner-mobile"><div className={styles["banner-photo-mobile"]}><Image src={bannerImageUrl} alt="" /></div><div className={styles["banner-content-mobile"]} style={{
        color: textColor
      }}>{bannerContent}</div></div> : <div className={styles["banner-container"]} style={{
      backgroundImage: `url(${bannerImageUrl})`
    }} data-purpose="instructor-community-banner"><div className={styles["banner-content"]} style={{
        color: textColor
      }}>{bannerContent}</div></div>;
    $[45] = bannerContent;
    $[46] = bannerImageUrl;
    $[47] = isMobile;
    $[48] = textColor;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const banner = t19;
  let t20;
  if ($[50] !== banner || $[51] !== handleImpression) {
    t20 = <TrackImpression trackFunc={handleImpression} testId="instructor-community-banner-impression">{banner}</TrackImpression>;
    $[50] = banner;
    $[51] = handleImpression;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
}